import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Rating,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Pagination
} from '@mui/material';
import {
  Business,
  LocationOn,
  Star,
  LocalShipping,
  CheckCircle,
  Search,
  FilterList,
  Visibility,
  ShoppingCart
} from '@mui/icons-material';
import api from '../../services/api';
import { brand } from '../../theme/colors';

const MerchantDirectory = ({ onSelectMerchant }) => {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    searchTerm: '',
    city: '',
    tier: '',
    specialization: '',
    minRating: '',
    verificationStatus: 'Verified'
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    // Debounce so typing in the search box doesn't fire a request per
    // keystroke; 350ms is imperceptible for the dropdown filters too.
    const timer = setTimeout(loadMerchants, 350);
    return () => clearTimeout(timer);
  }, [filters, page]);

  const loadMerchants = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: 12,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await api.get('/merchants', { params });

      if (response.data.success) {
        setMerchants(response.data.data || []);
        setTotalPages(Math.ceil((response.data.pagination?.total || 0) / 12));
      }
    } catch (err) {
      console.error('Error loading merchants:', err);
      setError('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'Tier3': return 'Premium Merchant';
      case 'Tier2': return 'Verified Merchant';
      case 'Tier1': return 'Basic Merchant';
      default: return 'Basic Merchant';
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Tier3': return 'success';
      case 'Tier2': return 'primary';
      case 'Tier1': return 'default';
      default: return 'default';
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      city: '',
      tier: '',
      specialization: '',
      minRating: '',
      verificationStatus: 'Verified'
    });
    setPage(1);
  };

  if (loading && merchants.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FilterList />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filter Suppliers
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Suppliers"
              placeholder="Business name, specialization..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>City</InputLabel>
              <Select
                value={filters.city}
                label="City"
                onChange={(e) => handleFilterChange('city', e.target.value)}
              >
                <MenuItem value="">All Cities</MenuItem>
                <MenuItem value="Harare">Harare</MenuItem>
                <MenuItem value="Bulawayo">Bulawayo</MenuItem>
                <MenuItem value="Chitungwiza">Chitungwiza</MenuItem>
                <MenuItem value="Mutare">Mutare</MenuItem>
                <MenuItem value="Gweru">Gweru</MenuItem>
                <MenuItem value="Norton">Norton</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tier Level</InputLabel>
              <Select
                value={filters.tier}
                label="Tier Level"
                onChange={(e) => handleFilterChange('tier', e.target.value)}
              >
                <MenuItem value="">All Tiers</MenuItem>
                <MenuItem value="Tier1">Tier 1 - Basic</MenuItem>
                <MenuItem value="Tier2">Tier 2 - Verified</MenuItem>
                <MenuItem value="Tier3">Tier 3 - Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Min Rating</InputLabel>
              <Select
                value={filters.minRating}
                label="Min Rating"
                onChange={(e) => handleFilterChange('minRating', e.target.value)}
              >
                <MenuItem value="">Any Rating</MenuItem>
                <MenuItem value="4.5">4.5+ Stars</MenuItem>
                <MenuItem value="4.0">4.0+ Stars</MenuItem>
                <MenuItem value="3.5">3.5+ Stars</MenuItem>
                <MenuItem value="3.0">3.0+ Stars</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Specialization</InputLabel>
              <Select
                value={filters.specialization}
                label="Specialization"
                onChange={(e) => handleFilterChange('specialization', e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="Cement & Concrete">Cement & Concrete</MenuItem>
                <MenuItem value="Bricks & Blocks">Bricks & Blocks</MenuItem>
                <MenuItem value="Steel & Rebar">Steel & Rebar</MenuItem>
                <MenuItem value="Roofing Materials">Roofing Materials</MenuItem>
                <MenuItem value="Timber">Timber</MenuItem>
                <MenuItem value="Plumbing">Plumbing</MenuItem>
                <MenuItem value="Electrical">Electrical</MenuItem>
                <MenuItem value="Paint & Finishes">Paint & Finishes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Results Count */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {merchants.length} supplier{merchants.length !== 1 ? 's' : ''} found
        </Typography>
      </Box>

      {/* Merchant Cards */}
      {merchants.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No suppliers found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search terms
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {merchants.map((merchant) => (
              <Grid item xs={12} sm={6} md={4} key={merchant.user_id || merchant.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Business sx={{ fontSize: 24, color: brand.primary }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {merchant.business_name || 'Unknown Business'}
                          </Typography>
                          {merchant.verification_status === 'Verified' && (
                            <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                          )}
                        </Box>
                        {merchant.trading_name && (
                          <Typography variant="body2" color="text.secondary">
                            Trading as: {merchant.trading_name}
                          </Typography>
                        )}
                        <Chip
                          label={getTierLabel(merchant.verification_tier)}
                          color={getTierColor(merchant.verification_tier)}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ fontSize: 18, color: '#ffc107' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {merchant.rating_average ? parseFloat(merchant.rating_average).toFixed(1) : '0.0'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          ({merchant.total_ratings || 0} ratings)
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {merchant.physical_address?.city || merchant.service_areas?.[0] || 'Location TBD'}
                        </Typography>
                      </Box>
                      {merchant.delivery_options?.offers_delivery && (
                        <Chip
                          icon={<LocalShipping sx={{ fontSize: 14 }} />}
                          label="Offers Delivery"
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Specializations:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {merchant.specializations?.slice(0, 3).map((spec, idx) => (
                          <Chip key={idx} label={spec} size="small" variant="outlined" />
                        ))}
                        {merchant.specializations?.length > 3 && (
                          <Chip label={`+${merchant.specializations.length - 3} more`} size="small" />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Orders Completed: {merchant.total_orders_completed || 0} | 
                        On-Time Rate: {merchant.on_time_delivery_rate || 0}%
                      </Typography>
                      {merchant.quote_response_time_avg && (
                        <Typography variant="body2" color="text.secondary">
                          Avg Response: {merchant.quote_response_time_avg} hours
                        </Typography>
                      )}
                    </Box>

                    {merchant.verification_tier === 'Tier3' && (
                      <Chip
                        label="🏆 PREMIUM SUPPLIER"
                        size="small"
                        color="success"
                        sx={{ mb: 2 }}
                      />
                    )}
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => {
                        if (onSelectMerchant) {
                          onSelectMerchant(merchant);
                        } else {
                          // Navigate to merchant details or quote request
                          navigate(`/suppliers/${merchant.user_id || merchant.id}`);
                        }
                      }}
                    >
                      View Profile
                    </Button>
                    <Tooltip title="Request Quote">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          // Navigate to merchant profile with quote request trigger
                          navigate(`/suppliers/${merchant.user_id || merchant.id}?requestQuote=true`);
                        }}
                      >
                        <ShoppingCart />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MerchantDirectory;

